import { attributesAtLevel } from '@jianghu/combat-engine';
import {
  ATTRIBUTE_INFO,
  ELEMENT_INFO,
  getItem,
  getQuest,
  getSect,
  type AttributeKey,
} from '@jianghu/game-data';
import { useEffect, useRef, useState } from 'react';
import { audio } from '../../game/audio/audio';
import { gameUi, useGame } from '../../game/store';
import { api } from '../../services/api';
import { useSession } from '../../stores/session-store';
import { SettingsPanel } from '../SettingsPanel';
import { useGameInstance } from './GameContext';
import { Icon } from './Icon';
import { npcName } from './QuestTracker';

function Head({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="window-head">
      <h3>{title}</h3>
      <button className="close-x" aria-label={`Close ${title}`} onClick={onClose}>
        ✕
      </button>
    </div>
  );
}

export function CharacterWindow() {
  const open = useGame((s) => s.windows.character);
  const stats = useGame((s) => s.stats);
  const self = useGame((s) => s.self);
  if (!open || !stats || !self) return null;
  const sect = getSect(self.sect);
  const attrs = sect ? attributesAtLevel(sect, self.level) : null;
  const rows: [string, string][] = [
    ['Health', String(stats.maxHp)],
    ['Qi', String(stats.maxMp)],
    ['Attack', String(stats.atk)],
    ['Qi Attack', String(stats.qiAtk)],
    ['Defence', String(stats.def)],
    ['Critical chance', `${(stats.crit * 100).toFixed(1)}%`],
    ['Evasion', `${(stats.evasion * 100).toFixed(1)}%`],
  ];
  return (
    <section className="window win-character" aria-label="Character">
      <Head title={self.name} onClose={() => gameUi.toggle('character', false)} />
      <div className="row" style={{ marginBottom: 10 }}>
        {sect && (
          <span className="element-badge" style={{ background: ELEMENT_INFO[sect.element].color }}>
            {ELEMENT_INFO[sect.element].nameZh}
          </span>
        )}
        <div>
          <div className="gold" style={{ fontFamily: 'var(--font-display)' }}>
            {sect?.name} · Level {self.level}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Power rating <strong className="gold">{stats.power}</strong>
          </div>
        </div>
      </div>
      {attrs && (
        <table className="stat-table" aria-label="Attributes">
          <tbody>
            {(Object.keys(attrs) as AttributeKey[]).map((k) => (
              <tr key={k} title={ATTRIBUTE_INFO[k].description}>
                <td>{ATTRIBUTE_INFO[k].name}</td>
                <td>{attrs[k]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ height: 10 }} />
      <table className="stat-table" aria-label="Combat statistics">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td>{k}</td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 11 }}>
        Attributes grow automatically with level. Manual attribute allocation arrives in a later milestone.
      </p>
    </section>
  );
}

export function QuestWindow() {
  const open = useGame((s) => s.windows.quests);
  const quests = useGame((s) => s.quests);
  const game = useGameInstance();
  if (!open) return null;
  const active = quests.filter((q) => q.status === 'active');
  const done = quests.filter((q) => q.status === 'completed');
  return (
    <section className="window win-quests" aria-label="Quest journal">
      <Head title="Quest Journal" onClose={() => gameUi.toggle('quests', false)} />
      {active.length === 0 && (
        <p className="muted">No active quests. Look for villagers marked with a golden “!”.</p>
      )}
      {active.map((q) => {
        const def = getQuest(q.id);
        if (!def) return null;
        return (
          <div key={q.id} style={{ marginBottom: 14 }}>
            <div className="row">
              <strong className="gold" style={{ fontFamily: 'var(--font-display)', fontSize: 16 }}>
                {def.name}
              </strong>
              {def.repeatable && <span className="muted">(daily)</span>}
              <span className="spacer" />
              <button className="btn btn-sm btn-ghost" onClick={() => game?.abandonQuest(q.id)}>
                Abandon
              </button>
            </div>
            <p className="muted" style={{ margin: '4px 0' }}>
              {def.summary}
            </p>
            {q.objectives.map((o) => (
              <div
                key={o.id}
                style={{ color: o.current >= o.required ? 'var(--jade-bright)' : 'var(--parchment)' }}
              >
                {o.current >= o.required ? '✓' : '·'} {o.label}{' '}
                {o.required > 1 ? `(${o.current}/${o.required})` : ''}
              </div>
            ))}
            {q.ready && <div style={{ color: '#ffd34a' }}>Return to {npcName(def.turnInNpcId)}.</div>}
            <div className="reward-row" style={{ marginTop: 4, fontSize: 12 }}>
              <span>Rewards:</span>
              <span>{def.rewards.xp} XP</span>
              <span>{def.rewards.coins} coins</span>
              {def.rewards.items.map((i) => (
                <span key={i.itemId}>{getItem(i.itemId)?.name}</span>
              ))}
            </div>
          </div>
        );
      })}
      {done.length > 0 && (
        <>
          <div className="panel-title" style={{ fontSize: 14, marginTop: 8 }}>
            Completed
          </div>
          {done.map((q) => (
            <div key={q.id} className="muted">
              ✓ {getQuest(q.id)?.name}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

export function DialogWindow() {
  const dialog = useGame((s) => s.dialog);
  const game = useGameInstance();
  const [offer, setOffer] = useState<string | null>(null);
  useEffect(() => setOffer(null), [dialog]);
  if (!dialog || !game) return null;
  const close = (): void => useGame.setState({ dialog: null });
  const def = offer ? getQuest(offer) : undefined;
  return (
    <section className="window win-dialog" role="dialog" aria-label={`Conversation with ${dialog.name}`}>
      <div className="window-head">
        <h3>
          {dialog.name}{' '}
          <span className="muted" style={{ fontSize: 13 }}>
            ‹{dialog.title}›
          </span>
        </h3>
        <button className="close-x" aria-label="Close" onClick={close}>
          ✕
        </button>
      </div>
      {def ? (
        <div className="quest-offer">
          <div className="gold" style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginBottom: 6 }}>
            {def.name}
          </div>
          <div className="dialog-lines">
            {def.dialogue.offer.map((l, i) => (
              <p key={i}>{l}</p>
            ))}
          </div>
          <div
            className="muted"
            style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}
          >
            Objectives
          </div>
          {def.objectives.map((o) => (
            <div key={o.id}>
              · {o.label}
              {o.type === 'kill' || o.type === 'collect' ? ` (${o.count})` : ''}
            </div>
          ))}
          <div className="reward-row" style={{ marginTop: 8 }}>
            <span>Rewards:</span>
            <span>{def.rewards.xp} XP</span>
            <span>{def.rewards.coins} coins</span>
            {def.rewards.items.map((i) => (
              <span key={i.itemId} className="gold">
                {getItem(i.itemId)?.name}
                {i.qty > 1 ? ` ×${i.qty}` : ''}
              </span>
            ))}
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="btn btn-primary"
              autoFocus
              onClick={() => {
                game.npcOp(dialog.npc, 'accept', def.id);
                close();
              }}
            >
              Accept
            </button>
            <button className="btn btn-ghost" onClick={() => setOffer(null)}>
              Not now
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="dialog-lines">
            {dialog.lines.map((l, i) => (
              <p key={i}>{l}</p>
            ))}
          </div>
          <div className="dialog-options">
            {dialog.options.map((o, i) => (
              <button
                key={i}
                className={`btn ${o.op === 'turnin' ? 'btn-primary' : o.op === 'accept' ? 'btn-jade' : ''}`}
                autoFocus={i === 0}
                onClick={() => {
                  audio.play('ui_click');
                  if (o.op === 'accept' && o.quest) setOffer(o.quest);
                  else if (o.op === 'turnin' && o.quest) game.npcOp(dialog.npc, 'turnin', o.quest);
                  else if (o.op === 'shop') game.npcOp(dialog.npc, 'shop');
                  else close();
                }}
              >
                {o.op === 'accept' ? '! ' : o.op === 'turnin' ? '? ' : ''}
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function ShopWindow() {
  const shop = useGame((s) => s.shop);
  const coins = useGame((s) => s.self?.coins ?? 0);
  const game = useGameInstance();
  if (!shop || !game) return null;
  return (
    <section className="window win-shop" aria-label={shop.name}>
      <Head title={shop.name} onClose={() => useGame.setState({ shop: null })} />
      {shop.sells.map((e) => {
        const def = getItem(e.itemId);
        return (
          <div key={e.itemId} className="shop-row">
            <div className="icon">
              <Icon name={def?.icon ?? 'empty'} />
            </div>
            <div style={{ flex: 1 }}>
              <div>{def?.name}</div>
              <div className="muted" style={{ fontSize: 11 }}>
                {def?.description}
              </div>
            </div>
            <div className="col" style={{ gap: 4, alignItems: 'flex-end' }}>
              <span className="coins">◉ {e.price}</span>
              <div className="row" style={{ gap: 4 }}>
                <button
                  className="btn btn-sm"
                  disabled={coins < e.price}
                  onClick={() => game.shopOp(shop.npc, 'buy', e.itemId, 1)}
                >
                  Buy
                </button>
                <button
                  className="btn btn-sm"
                  disabled={coins < e.price * 5}
                  onClick={() => game.shopOp(shop.npc, 'buy', e.itemId, 5)}
                >
                  ×5
                </button>
              </div>
            </div>
          </div>
        );
      })}
      <p className="muted" style={{ fontSize: 12 }}>
        Your coins: <span className="coins">{coins}</span>. To sell, select an item in your bag and press Sell
        (or double-click it).
      </p>
    </section>
  );
}

export function WaypointWindow() {
  const open = useGame((s) => s.windows.waypoints);
  const game = useGameInstance();
  if (!open || !game?.zone) return null;
  const me = game.selfPosition();
  return (
    <section className="window win-waypoints" aria-label="Waypoint travel">
      <Head title="Waypoint Shrines" onClose={() => gameUi.toggle('waypoints', false)} />
      <p className="muted">
        Channel your qi through the jade crystal to travel between shrines you stand beside.
      </p>
      <div className="col">
        {game.zone.waypoints.map((w) => {
          const here = me ? Math.hypot(me.x - w.x, me.z - w.z) < 6 : false;
          return (
            <button key={w.id} className="btn btn-block" disabled={here} onClick={() => game.teleport(w.id)}>
              {w.name} {here ? '(you are here)' : ''}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function MapWindow() {
  const open = useGame((s) => s.windows.map);
  const game = useGameInstance();
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!open || !game) return;
    const draw = (): void => {
      const me = game.selfPosition();
      if (!ref.current || !game.map || !me || !game.zone) return;
      game.map.drawWorldMap(
        ref.current,
        me.x,
        me.z,
        me.facing,
        game.markers(),
        game.zone.areas.map((a) => ({ x: a.x, z: a.z - a.r * 0.55, text: a.name })),
      );
    };
    draw();
    const id = window.setInterval(draw, 250);
    return () => window.clearInterval(id);
  }, [open, game]);
  if (!open) return null;
  const size = Math.min(640, window.innerWidth - 60, window.innerHeight - 140);
  return (
    <section className="window win-map" aria-label="World map">
      <Head title={game?.zone?.name ?? 'Map'} onClose={() => gameUi.toggle('map', false)} />
      <canvas ref={ref} width={size} height={size} style={{ display: 'block', borderRadius: 4 }} />
      <div className="row muted" style={{ fontSize: 11, marginTop: 6, gap: 14, flexWrap: 'wrap' }}>
        <span style={{ color: '#ffd34a' }}>! quest</span>
        <span style={{ color: '#ffd34a' }}>? turn in</span>
        <span style={{ color: '#6fd1a6' }}>● waypoint</span>
        <span style={{ color: '#e0574a' }}>● enemy</span>
        <span style={{ color: '#8cc1f2' }}>● player</span>
        <span>North is up</span>
      </div>
    </section>
  );
}

export function MenuWindow({ onLeave }: { onLeave: () => void }) {
  const open = useGame((s) => s.windows.menu);
  if (!open) return null;
  const logout = async (): Promise<void> => {
    await api.logout().catch(() => {});
    useSession.getState().setUser(null);
    onLeave();
    useSession.getState().go('auth');
  };
  return (
    <section className="window win-menu" role="dialog" aria-label="Game menu">
      <Head title="Menu" onClose={() => gameUi.toggle('menu', false)} />
      <SettingsPanel />
      <div className="col" style={{ marginTop: 12 }}>
        <button className="btn btn-block" onClick={() => gameUi.toggle('help', true)}>
          Controls
        </button>
        <button className="btn btn-block" onClick={onLeave}>
          Character select
        </button>
        <button className="btn btn-ghost btn-block" onClick={() => void logout()}>
          Log out
        </button>
        <button className="btn btn-primary btn-block" onClick={() => gameUi.toggle('menu', false)}>
          Resume
        </button>
      </div>
    </section>
  );
}

const CONTROLS: [string, string][] = [
  ['W A S D / arrows', 'Move (camera-relative)'],
  ['Shift', 'Sprint (uses stamina)'],
  ['Space', 'Dodge — brief invulnerability'],
  ['Left mouse / 1', 'Sword combo (hold to chain)'],
  ['2 · 3 · 4', 'Techniques'],
  ['5 · 6', 'Healing / Qi pills'],
  ['Click / Tab', 'Select target / next enemy'],
  ['F', 'Talk, pick up loot, use shrine'],
  ['Q / E, right-drag', 'Rotate camera'],
  ['Mouse wheel', 'Zoom'],
  ['I · C · L · M', 'Inventory · Character · Quests · Map'],
  ['Enter', 'Chat'],
  ['Esc', 'Close windows / menu'],
];

export function HelpWindow() {
  const open = useGame((s) => s.windows.help);
  if (!open) return null;
  return (
    <section className="window win-help" aria-label="Controls">
      <Head title="Controls" onClose={() => gameUi.toggle('help', false)} />
      <table className="stat-table">
        <tbody>
          {CONTROLS.map(([k, v]) => (
            <tr key={k}>
              <td className="gold">{k}</td>
              <td style={{ textAlign: 'right', fontWeight: 400 }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
